import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Command, LabelCommand } from "@tylertech/commands-module";
import { LgdFormControlFactory } from "@tylertech/lgd-reactive-forms";
import { ToastService } from "@tylertech/core-module";
import { ExecutionResult, ILocationCode, LocationCodeViewModel } from "@tylertech/tyler-api-types";
import { InventoryLocationService, LocationCodeLookupService } from "../../services";
import { HttpErrorResponse } from "@angular/common/http";
export interface NewLocationDialogData
{
    readonly description: string;
    readonly parentLocation: LocationCodeViewModel;
}
@Component({
    selector: "new-location-dialog-component",
    templateUrl: "./addNewLocationDialog.tmpl.html",
    styleUrls: ["./addNewLocationDialog.component.scss"],
    standalone: false
})
export class NewLocationDialogComponent implements OnInit, OnDestroy
{
    private readonly destructor$ = new Subject<void>();
    private readonly locationSubject = new BehaviorSubject<Array<LocationCodeViewModel>>([]);
    readonly formGroup: FormGroup;
    readonly commands: Array<Command>;
    private newLocation: ILocationCode;
    private selectedLocation: LocationCodeViewModel;
    saving: boolean;
    constructor(
        private readonly dialogRef: MatDialogRef<NewLocationDialogComponent, ILocationCode | undefined>,
        @Inject(MAT_DIALOG_DATA)
        private readonly data: NewLocationDialogData,
        private readonly $toastService: ToastService,
        private readonly $locationService: InventoryLocationService,
        private readonly $inventoryLocationService: LocationCodeLookupService
    )
    {
        this.commands = [
            new LabelCommand({
                label: "Cancel",
                action: () => this.cancel(),
                getEnabled: () => true,
            }),
            new LabelCommand({
                label: "Save & Close",
                action: () => this.save(),
                getEnabled: () => this.formGroup.valid,
            }),
        ];
        this.formGroup = new FormGroup({
            district: LgdFormControlFactory.typeahead<LocationCodeViewModel>(undefined, true, {
                displayValueFn: (x: LocationCodeViewModel) => (x ? `${x.Code} - ${x.Description}` : ""),
            }),
            code: LgdFormControlFactory.text(undefined, true, { maxLength: 20 }),
            description: LgdFormControlFactory.text(undefined, true, { maxLength: 50 }),
        });
    }

    private get parentLocation(): LocationCodeViewModel
    {
        return this.data.parentLocation;
    }

    private get description(): string
    {
        return this.data.description;
    }

    get Description(): string
    {
        return this.newLocation.Description;
    }

    set Description(value: string)
    {
        this.newLocation.Description = value;
    }

    get Code(): string
    {
        return this.newLocation.Code;
    }

    set Code(value: string)
    {
        this.newLocation.Code = value;
    }

    get locationObservable(): Observable<Array<unknown>>
    {
        return this.locationSubject.asObservable();
    }

    private get districtControl(): AbstractControl
    {
        return this.formGroup.get("district");
    }

    private get codeControl(): AbstractControl
    {
        return this.formGroup.get("code");
    }

    private get descriptionControl(): AbstractControl
    {
        return this.formGroup.get("description");
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;
        this.newLocation = this.data.description
            ? {
                Id: undefined,
                Code: this.description.length > 20 ? this.data.description.substring(0, 20) : this.data.description,
                Description:
                    this.data.description.length > 50
                        ? this.data.description.substring(0, 50)
                        : this.data.description,
                DefaultTransactionCode: undefined,
                LocationItems: undefined,
                ParentId: undefined,
            }
            : {
                Id: undefined,
                Code: undefined,
                Description: undefined,
                DefaultTransactionCode: undefined,
                LocationItems: undefined,
                ParentId: undefined,
            };
        this.selectedLocation = this.data.parentLocation;
        this.districtControl.setValue(this.parentLocation, { emitEvent: false });
        this.codeControl.setValue(this.newLocation.Code, { emitEvent: false });
        this.descriptionControl.setValue(this.newLocation.Description, { emitEvent: false });
        subscribeToValueChanges(this.codeControl, (newValue: string) =>
        {
            this.newLocation.Code = newValue;
        });
        subscribeToValueChanges(this.descriptionControl, (newValue: string) =>
        {
            this.newLocation.Description = newValue;
        });
        function subscribeToValueChanges<T>(control: AbstractControl, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$)).subscribe(x =>
            {
                subscriptionFn(x);
            });
        }
    }

    private cancel(): void
    {
        this.dialogRef.close();
    }

    private save(): void
    {
        this.saving = true;
        this.newLocation.ParentId = this.selectedLocation.Id;
        this.$locationService
            .AddStockLocation(this.newLocation)
            .then(
                (result: ILocationCode): void =>
                {
                    this.$toastService.showToast("New Location was added successfully");
                    this.dialogRef.close(result);
                },
                (result: HttpErrorResponse): void =>
                {
                    this.$toastService.showToast(result.error.Message);
                }
            )
            .finally(() =>
            {
                this.saving = false;
            });
    }

    lookupDistricts($event: string): void
    {
        this.$inventoryLocationService
            .Lookup({ SearchText: $event })
            .then((executionResult: ExecutionResult<Array<LocationCodeViewModel>>) =>
            {
                this.locationSubject.next(executionResult.Result);
            });
    }
}
